import { RequestBuilder } from "../requestBuilder";
import {
  AddGitHubIntegrationProjectInputSchema,
  AddGoogleWorkspaceProjectInputSchema,
  AddLinearIntegrationProjectInputSchema,
  AddNotionIntegrationProjectInputSchema,
  AddSlackIntegrationProjectInputSchema,
  BindSlackChannelInputSchema,
  CreateGitHubIntegrationConfigInputSchema,
  CreateGoogleWorkspaceConfigInputSchema,
  CreateLinearIntegrationConfigInputSchema,
  CreateNotionIntegrationConfigInputSchema,
  CreateSlackIntegrationConfigInputSchema,
  EntitiesQueryParamsSchema,
  EntitiesResponseSchema,
  EntityDetailResponseSchema,
  EntityIdParamsSchema,
  GitHubDiscoverResponseSchema,
  GitHubIntegrationConfigParamsSchema,
  GitHubIntegrationConfigSchema,
  GitHubIntegrationConfigsResponseSchema,
  GitHubIntegrationProjectParamsSchema,
  GitHubWebhookInfoSchema,
  GoogleCalendarDefaultSchema,
  GoogleCalendarPreferenceSchema,
  GoogleDeploymentResponseSchema,
  GoogleDiscoverResponseSchema,
  GoogleFolderCountResponseSchema,
  GoogleScopesResponseSchema,
  GoogleVerifyScopesResponseSchema,
  GoogleWorkspaceConfigParamsSchema,
  GoogleWorkspaceConfigSchema,
  GoogleWorkspaceConfigsResponseSchema,
  GoogleWorkspaceDiscoverParamsSchema,
  GoogleWorkspaceFolderCountParamsSchema,
  GoogleWorkspaceProjectParamsSchema,
  LinearDiscoverResponseSchema,
  LinearIntegrationConfigParamsSchema,
  LinearIntegrationConfigSchema,
  LinearIntegrationConfigsResponseSchema,
  LinearIntegrationProjectParamsSchema,
  LinearWebhookInfoSchema,
  MentionablesQueryParamsSchema,
  MentionablesResponseSchema,
  NotionDatabasePagesResponseSchema,
  NotionDatabaseViewsParamsSchema,
  NotionDiscoverResponseSchema,
  NotionIntegrationConfigParamsSchema,
  NotionIntegrationConfigSchema,
  NotionIntegrationConfigsResponseSchema,
  NotionIntegrationProjectParamsSchema,
  NotionProjectDatabasePagesParamsSchema,
  NotionProjectIntegrationResponseSchema,
  NotionViewCountParamsSchema,
  NotionViewCountResponseSchema,
  NotionViewsResponseSchema,
  NotionWebhookInfoSchema,
  OAuthRequestInputSchema,
  OAuthRequestResponseSchema,
  RotateNotionIntegrationTokenInputSchema,
  SetSlackAppNameInputSchema,
  SetSlackChannelActionEnabledInputSchema,
  SetSlackSigningSecretInputSchema,
  SlackAccountsResponseSchema,
  SlackChannelActionParamsSchema,
  SlackChannelActionSchema,
  SlackChannelBindingSchema,
  SlackChannelBindingsResponseSchema,
  SlackChannelSettingsSchema,
  SlackChannelsResponseSchema,
  SlackIntegrationChannelParamsSchema,
  SlackIntegrationConfigParamsSchema,
  SlackIntegrationConfigSchema,
  SlackIntegrationConfigsResponseSchema,
  SlackIntegrationProjectParamsSchema,
  SlackManifestParamsSchema,
  SlackManifestResponseSchema,
  SlackProjectChannelsResponseSchema,
  UpdateGitHubCredentialsInputSchema,
  UpdateGitHubSyncModeInputSchema,
  UpdateGitHubWebhookSecretInputSchema,
  UpdateGoogleCalendarPreferenceInputSchema,
  UpdateGoogleWorkspaceSubjectInputSchema,
  UpdateGoogleWorkspaceSyncModeInputSchema,
  UpdateLinearCredentialsInputSchema,
  UpdateLinearSyncModeInputSchema,
  UpdateLinearWebhookSecretInputSchema,
  UpdateNotionSyncModeInputSchema,
  UpdateSlackChannelSettingsInputSchema,
} from "./integrationsClient.types";

// POST /oauth-requests - Initiate OAuth flow
export const OAuthRequestConfig = new RequestBuilder({
  path: () => "/oauth-requests",
  bodySchema: OAuthRequestInputSchema,
  responseSchema: OAuthRequestResponseSchema,
})
  .withMethod("POST")
  .withIncludeProjectId();

// GET /integrations/google - List org-level Google Workspace configs
export const GoogleWorkspaceConfigsRequestConfig = new RequestBuilder({
  path: () => "/integrations/google",
  responseSchema: GoogleWorkspaceConfigsResponseSchema,
}).withIncludeOrganizationId();

// GET /integrations/google/scopes - Required delegation scopes
export const GoogleWorkspaceScopesRequestConfig = new RequestBuilder({
  path: () => "/integrations/google/scopes",
  responseSchema: GoogleScopesResponseSchema,
}).withIncludeOrganizationId();

// POST /integrations/google/configs/{integrationId}/scopes/verify - Live per-scope health probe
export const VerifyGoogleWorkspaceScopesRequestConfig = new RequestBuilder({
  paramsSchema: GoogleWorkspaceConfigParamsSchema,
  path: (params) => `/integrations/google/configs/${params.integrationId}/scopes/verify`,
  responseSchema: GoogleVerifyScopesResponseSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

// POST /integrations/google - Create Google Workspace config
export const CreateGoogleWorkspaceConfigRequestConfig = new RequestBuilder({
  path: () => "/integrations/google",
  bodySchema: CreateGoogleWorkspaceConfigInputSchema,
  responseSchema: GoogleWorkspaceConfigSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

// DELETE /integrations/google/configs/{integrationId} - Delete Google Workspace config
export const DeleteGoogleWorkspaceConfigRequestConfig = new RequestBuilder({
  paramsSchema: GoogleWorkspaceConfigParamsSchema,
  path: (params) => `/integrations/google/configs/${params.integrationId}`,
})
  .withMethod("DELETE")
  .withIncludeOrganizationId();

// POST /integrations/google/configs/{integrationId}/projects - Add project to config
export const AddGoogleWorkspaceProjectRequestConfig = new RequestBuilder({
  paramsSchema: GoogleWorkspaceConfigParamsSchema,
  path: (params) => `/integrations/google/configs/${params.integrationId}/projects`,
  bodySchema: AddGoogleWorkspaceProjectInputSchema,
  responseSchema: GoogleWorkspaceConfigSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

// DELETE /integrations/google/configs/{integrationId}/projects/{projectId} - Remove project from config
export const RemoveGoogleWorkspaceProjectRequestConfig = new RequestBuilder({
  paramsSchema: GoogleWorkspaceProjectParamsSchema,
  path: (params) => `/integrations/google/configs/${params.integrationId}/projects/${params.projectId}`,
})
  .withMethod("DELETE")
  .withIncludeOrganizationId();

// PUT /integrations/google/configs/{integrationId}/subject - Set or clear the SA's impersonation subject
export const UpdateGoogleWorkspaceSubjectRequestConfig = new RequestBuilder({
  paramsSchema: GoogleWorkspaceConfigParamsSchema,
  path: (params) => `/integrations/google/configs/${params.integrationId}/subject`,
  bodySchema: UpdateGoogleWorkspaceSubjectInputSchema,
  responseSchema: GoogleWorkspaceConfigSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

// GET /integrations/google/configs/{integrationId}/discover?parentId=&driveId=&resolveIds= - Lazy per-level Drive tree
export const GoogleWorkspaceDiscoverRequestConfig = new RequestBuilder({
  paramsSchema: GoogleWorkspaceDiscoverParamsSchema,
  path: (params) => `/integrations/google/configs/${params.integrationId}/discover`,
  responseSchema: GoogleDiscoverResponseSchema,
})
  .withQueryParams((params) => {
    const query: Record<string, string> = {};
    if (params.parentId) query.parentId = params.parentId;
    if (params.driveId) query.driveId = params.driveId;
    if (params.resolveIds?.length) query.resolveIds = params.resolveIds.join(",");
    return query;
  })
  .withIncludeOrganizationId();

// GET /integrations/google/configs/{integrationId}/discover/count?folderId=&driveId= - One page of a folder's file count
export const GoogleWorkspaceFolderCountRequestConfig = new RequestBuilder({
  paramsSchema: GoogleWorkspaceFolderCountParamsSchema,
  path: (params) => `/integrations/google/configs/${params.integrationId}/discover/count`,
  responseSchema: GoogleFolderCountResponseSchema,
})
  .withQueryParams((params) => {
    const query: Record<string, string> = { folderId: params.folderId };
    if (params.driveId) query.driveId = params.driveId;
    return query;
  })
  .withIncludeOrganizationId();

// GET /integrations/google/configs/{integrationId}/projects/{projectId} - A deployment's root selection
export const GoogleWorkspaceDeploymentRequestConfig = new RequestBuilder({
  paramsSchema: GoogleWorkspaceProjectParamsSchema,
  path: (params) => `/integrations/google/configs/${params.integrationId}/projects/${params.projectId}`,
  responseSchema: GoogleDeploymentResponseSchema,
}).withIncludeOrganizationId();

// PUT /integrations/google/configs/{integrationId}/projects/{projectId}/sync-mode - Set a deployment's sync mode and roots
export const UpdateGoogleWorkspaceSyncModeRequestConfig = new RequestBuilder({
  paramsSchema: GoogleWorkspaceProjectParamsSchema,
  path: (params) => `/integrations/google/configs/${params.integrationId}/projects/${params.projectId}/sync-mode`,
  bodySchema: UpdateGoogleWorkspaceSyncModeInputSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

// POST /integrations/google/configs/{integrationId}/projects/{projectId}/sync - Queue an immediate pruning re-sync
export const SyncGoogleWorkspaceDeploymentRequestConfig = new RequestBuilder({
  paramsSchema: GoogleWorkspaceProjectParamsSchema,
  path: (params) => `/integrations/google/configs/${params.integrationId}/projects/${params.projectId}/sync`,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

// GET /integrations/google/calendar/me - The caller's own calendar auto-scheduling preference
export const GoogleCalendarPreferenceRequestConfig = new RequestBuilder({
  path: () => "/integrations/google/calendar/me",
  responseSchema: GoogleCalendarPreferenceSchema,
}).withIncludeOrganizationId();

// PUT /integrations/google/calendar/me - Save the caller's own calendar auto-scheduling preference
export const UpdateGoogleCalendarPreferenceRequestConfig = new RequestBuilder({
  path: () => "/integrations/google/calendar/me",
  bodySchema: UpdateGoogleCalendarPreferenceInputSchema,
  responseSchema: GoogleCalendarPreferenceSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

// PUT /integrations/google/configs/{integrationId}/calendar - Set the org-wide pre-selection
export const UpdateGoogleCalendarDefaultRequestConfig = new RequestBuilder({
  paramsSchema: GoogleWorkspaceConfigParamsSchema,
  path: (params) => `/integrations/google/configs/${params.integrationId}/calendar`,
  bodySchema: GoogleCalendarDefaultSchema,
  responseSchema: GoogleCalendarDefaultSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

// GET /integrations/slack - List org-level Slack integration configs
export const SlackIntegrationConfigsRequestConfig = new RequestBuilder({
  path: () => "/integrations/slack",
  responseSchema: SlackIntegrationConfigsResponseSchema,
}).withIncludeOrganizationId();

// GET /users/integrations/slack - Slack workspaces the caller can link their own account to (bearer only)
export const SlackAccountsRequestConfig = new RequestBuilder({
  path: () => "/users/integrations/slack",
  responseSchema: SlackAccountsResponseSchema,
});

// DELETE /users/integrations/slack/{integrationId} - Unlink the caller's own Slack account from a workspace (bearer only)
export const DisconnectSlackAccountRequestConfig = new RequestBuilder({
  paramsSchema: SlackIntegrationConfigParamsSchema,
  path: (params) => `/users/integrations/slack/${params.integrationId}`,
}).withMethod("DELETE");

// GET /integrations/slack/manifest?appName= - Pre-filled Slack app manifest for the BYO create-app flow
export const SlackManifestRequestConfig = new RequestBuilder({
  paramsSchema: SlackManifestParamsSchema,
  path: () => "/integrations/slack/manifest",
  responseSchema: SlackManifestResponseSchema,
})
  .withQueryParams((params) => ({ appName: params.appName }))
  .withIncludeOrganizationId();

// GET /integrations/slack/configs/{integrationId}/manifest - Manifest re-rendered with this config's request URLs
export const SlackManifestForConfigRequestConfig = new RequestBuilder({
  paramsSchema: SlackIntegrationConfigParamsSchema,
  path: (params) => `/integrations/slack/configs/${params.integrationId}/manifest`,
  responseSchema: SlackManifestResponseSchema,
}).withIncludeOrganizationId();

// PUT /integrations/slack/configs/{integrationId}/app-name - Rename what the manifest calls the app
export const SetSlackAppNameRequestConfig = new RequestBuilder({
  paramsSchema: SlackIntegrationConfigParamsSchema,
  path: (params) => `/integrations/slack/configs/${params.integrationId}/app-name`,
  bodySchema: SetSlackAppNameInputSchema,
  responseSchema: SlackIntegrationConfigSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

// PUT /integrations/slack/configs/{integrationId}/signing-secret - Store the secret inbound deliveries verify against
export const SetSlackSigningSecretRequestConfig = new RequestBuilder({
  paramsSchema: SlackIntegrationConfigParamsSchema,
  path: (params) => `/integrations/slack/configs/${params.integrationId}/signing-secret`,
  bodySchema: SetSlackSigningSecretInputSchema,
  responseSchema: SlackIntegrationConfigSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

// GET /integrations/slack/configs/{integrationId}/slack-channels - Channels the bot can see in the workspace
export const SlackWorkspaceChannelsRequestConfig = new RequestBuilder({
  paramsSchema: SlackIntegrationConfigParamsSchema,
  path: (params) => `/integrations/slack/configs/${params.integrationId}/slack-channels`,
  responseSchema: SlackChannelsResponseSchema,
}).withIncludeOrganizationId();

// GET /integrations/slack/configs/{integrationId}/channels - Channel-to-project bindings
export const SlackChannelBindingsRequestConfig = new RequestBuilder({
  paramsSchema: SlackIntegrationConfigParamsSchema,
  path: (params) => `/integrations/slack/configs/${params.integrationId}/channels`,
  responseSchema: SlackChannelBindingsResponseSchema,
}).withIncludeOrganizationId();

// POST /integrations/slack/configs/{integrationId}/channels - Route one channel's messages to one project
export const BindSlackChannelRequestConfig = new RequestBuilder({
  paramsSchema: SlackIntegrationConfigParamsSchema,
  path: (params) => `/integrations/slack/configs/${params.integrationId}/channels`,
  bodySchema: BindSlackChannelInputSchema,
  responseSchema: SlackChannelBindingSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

// GET /integrations/slack/configs/{integrationId}/channels/{channelId} - One bound channel's settings
export const SlackChannelSettingsRequestConfig = new RequestBuilder({
  paramsSchema: SlackIntegrationChannelParamsSchema,
  path: (params) =>
    `/integrations/slack/configs/${encodeURIComponent(params.integrationId)}/channels/${encodeURIComponent(params.channelId)}`,
  responseSchema: SlackChannelSettingsSchema,
}).withIncludeOrganizationId();

// PATCH /integrations/slack/configs/{integrationId}/channels/{channelId} - Change a bound channel's settings
export const UpdateSlackChannelSettingsRequestConfig = new RequestBuilder({
  paramsSchema: SlackIntegrationChannelParamsSchema,
  path: (params) =>
    `/integrations/slack/configs/${encodeURIComponent(params.integrationId)}/channels/${encodeURIComponent(params.channelId)}`,
  bodySchema: UpdateSlackChannelSettingsInputSchema,
  responseSchema: SlackChannelSettingsSchema,
})
  .withMethod("PATCH")
  .withIncludeOrganizationId();

// PATCH /integrations/slack/configs/{integrationId}/channels/{channelId}/actions/{actionId} - Pause or resume an action posting to the channel
export const SetSlackChannelActionEnabledRequestConfig = new RequestBuilder({
  paramsSchema: SlackChannelActionParamsSchema,
  path: (params) =>
    `/integrations/slack/configs/${encodeURIComponent(params.integrationId)}/channels/${encodeURIComponent(params.channelId)}/actions/${encodeURIComponent(params.actionId)}`,
  bodySchema: SetSlackChannelActionEnabledInputSchema,
  responseSchema: SlackChannelActionSchema,
})
  .withMethod("PATCH")
  .withIncludeOrganizationId();

// DELETE /integrations/slack/configs/{integrationId}/channels/{channelId}/actions/{actionId} - Delete an action posting to the channel
export const DeleteSlackChannelActionRequestConfig = new RequestBuilder({
  paramsSchema: SlackChannelActionParamsSchema,
  path: (params) =>
    `/integrations/slack/configs/${encodeURIComponent(params.integrationId)}/channels/${encodeURIComponent(params.channelId)}/actions/${encodeURIComponent(params.actionId)}`,
})
  .withMethod("DELETE")
  .withIncludeOrganizationId();

// GET /integrations/slack/channels - Channels the focused project's actions may post to
export const SlackProjectChannelsRequestConfig = new RequestBuilder({
  path: () => "/integrations/slack/channels",
  responseSchema: SlackProjectChannelsResponseSchema,
}).withIncludeProjectId();

// DELETE /integrations/slack/configs/{integrationId}/channels/{channelId} - Stop routing one channel
export const UnbindSlackChannelRequestConfig = new RequestBuilder({
  paramsSchema: SlackIntegrationChannelParamsSchema,
  path: (params) => `/integrations/slack/configs/${params.integrationId}/channels/${params.channelId}`,
})
  .withMethod("DELETE")
  .withIncludeOrganizationId();

// POST /integrations/slack - Create Slack integration config
export const CreateSlackIntegrationConfigRequestConfig = new RequestBuilder({
  path: () => "/integrations/slack",
  bodySchema: CreateSlackIntegrationConfigInputSchema,
  responseSchema: SlackIntegrationConfigSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

// DELETE /integrations/slack/configs/{integrationId} - Delete Slack integration config
export const DeleteSlackIntegrationConfigRequestConfig = new RequestBuilder({
  paramsSchema: SlackIntegrationConfigParamsSchema,
  path: (params) => `/integrations/slack/configs/${params.integrationId}`,
})
  .withMethod("DELETE")
  .withIncludeOrganizationId();

// POST /integrations/slack/configs/{integrationId}/projects - Add project to config
export const AddSlackIntegrationProjectRequestConfig = new RequestBuilder({
  paramsSchema: SlackIntegrationConfigParamsSchema,
  path: (params) => `/integrations/slack/configs/${params.integrationId}/projects`,
  bodySchema: AddSlackIntegrationProjectInputSchema,
  responseSchema: SlackIntegrationConfigSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

// DELETE /integrations/slack/configs/{integrationId}/projects/{projectId} - Remove project from config
export const RemoveSlackIntegrationProjectRequestConfig = new RequestBuilder({
  paramsSchema: SlackIntegrationProjectParamsSchema,
  path: (params) => `/integrations/slack/configs/${params.integrationId}/projects/${params.projectId}`,
})
  .withMethod("DELETE")
  .withIncludeOrganizationId();

// GET /integrations/notion - List org-level Notion integration configs
export const NotionIntegrationConfigsRequestConfig = new RequestBuilder({
  path: () => "/integrations/notion",
  responseSchema: NotionIntegrationConfigsResponseSchema,
}).withIncludeOrganizationId();

// POST /integrations/notion - Create Notion integration config from a pasted token
export const CreateNotionIntegrationConfigRequestConfig = new RequestBuilder({
  path: () => "/integrations/notion",
  bodySchema: CreateNotionIntegrationConfigInputSchema,
  responseSchema: NotionIntegrationConfigSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

// DELETE /integrations/notion/configs/{integrationId} - Delete Notion integration config
export const DeleteNotionIntegrationConfigRequestConfig = new RequestBuilder({
  paramsSchema: NotionIntegrationConfigParamsSchema,
  path: (params) => `/integrations/notion/configs/${params.integrationId}`,
})
  .withMethod("DELETE")
  .withIncludeOrganizationId();

// PUT /integrations/notion/configs/{integrationId}/token - Replace the stored bot token and queue a full re-sync
export const RotateNotionIntegrationTokenRequestConfig = new RequestBuilder({
  paramsSchema: NotionIntegrationConfigParamsSchema,
  path: (params) => `/integrations/notion/configs/${params.integrationId}/token`,
  bodySchema: RotateNotionIntegrationTokenInputSchema,
  responseSchema: NotionIntegrationConfigSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

// GET /integrations/notion/configs/{integrationId}/webhook - Webhook URL + verification token for the connect flow
export const NotionWebhookRequestConfig = new RequestBuilder({
  paramsSchema: NotionIntegrationConfigParamsSchema,
  path: (params) => `/integrations/notion/configs/${params.integrationId}/webhook`,
  responseSchema: NotionWebhookInfoSchema,
}).withIncludeOrganizationId();

// POST /integrations/notion/configs/{integrationId}/webhook/verify - Open a 15-min window during which Notion's next handshake token is stored
export const BeginNotionWebhookVerificationRequestConfig = new RequestBuilder({
  paramsSchema: NotionIntegrationConfigParamsSchema,
  path: (params) => `/integrations/notion/configs/${params.integrationId}/webhook/verify`,
  responseSchema: NotionWebhookInfoSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

// GET /integrations/notion/configs/{integrationId}/discover - Integration-accessible page/database tree
export const NotionDiscoverRequestConfig = new RequestBuilder({
  paramsSchema: NotionIntegrationConfigParamsSchema,
  path: (params) => `/integrations/notion/configs/${params.integrationId}/discover`,
  responseSchema: NotionDiscoverResponseSchema,
}).withIncludeOrganizationId();

// GET /integrations/notion/configs/{integrationId}/databases/{databaseId}/pages - Rows of a database, newest-edited first
export const NotionDatabasePagesRequestConfig = new RequestBuilder({
  paramsSchema: NotionDatabaseViewsParamsSchema,
  path: (params) => `/integrations/notion/configs/${params.integrationId}/databases/${params.databaseId}/pages`,
  responseSchema: NotionDatabasePagesResponseSchema,
}).withIncludeOrganizationId();

// GET /integrations/notion/configs/{integrationId}/databases/{databaseId}/views - Views (tabs) selectable for a database root
export const NotionDatabaseViewsRequestConfig = new RequestBuilder({
  paramsSchema: NotionDatabaseViewsParamsSchema,
  path: (params) => `/integrations/notion/configs/${params.integrationId}/databases/${params.databaseId}/views`,
  responseSchema: NotionViewsResponseSchema,
}).withIncludeOrganizationId();

// GET /integrations/notion/configs/{integrationId}/databases/{databaseId}/views/{viewId}/count - One view's matching row count
export const NotionViewCountRequestConfig = new RequestBuilder({
  paramsSchema: NotionViewCountParamsSchema,
  path: (params) =>
    `/integrations/notion/configs/${params.integrationId}/databases/${params.databaseId}/views/${params.viewId}/count`,
  responseSchema: NotionViewCountResponseSchema,
}).withIncludeOrganizationId();

// POST /integrations/notion/configs/{integrationId}/projects - Deploy to a project with a page selection
export const AddNotionIntegrationProjectRequestConfig = new RequestBuilder({
  paramsSchema: NotionIntegrationConfigParamsSchema,
  path: (params) => `/integrations/notion/configs/${params.integrationId}/projects`,
  bodySchema: AddNotionIntegrationProjectInputSchema,
  responseSchema: NotionIntegrationConfigSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

// DELETE /integrations/notion/configs/{integrationId}/projects/{projectId} - Undeploy from a project
export const RemoveNotionIntegrationProjectRequestConfig = new RequestBuilder({
  paramsSchema: NotionIntegrationProjectParamsSchema,
  path: (params) => `/integrations/notion/configs/${params.integrationId}/projects/${params.projectId}`,
})
  .withMethod("DELETE")
  .withIncludeOrganizationId();

// PUT /integrations/notion/configs/{integrationId}/projects/{projectId}/sync-mode - Set a deployment's sync mode and page selection
export const UpdateNotionSyncModeRequestConfig = new RequestBuilder({
  paramsSchema: NotionIntegrationProjectParamsSchema,
  path: (params) => `/integrations/notion/configs/${params.integrationId}/projects/${params.projectId}/sync-mode`,
  bodySchema: UpdateNotionSyncModeInputSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

// GET /integrations/notion/project - Project member's view of the deployed Notion integration; 404 when not deployed or not a member
export const NotionProjectIntegrationRequestConfig = new RequestBuilder({
  path: () => "/integrations/notion/project",
  responseSchema: NotionProjectIntegrationResponseSchema,
}).withIncludeProjectId();

// GET /integrations/notion/project/discover - Integration-accessible page/database tree for a project member
export const NotionProjectDiscoverRequestConfig = new RequestBuilder({
  path: () => "/integrations/notion/project/discover",
  responseSchema: NotionDiscoverResponseSchema,
}).withIncludeProjectId();

// GET /integrations/notion/project/databases/{databaseId}/pages - Rows of a database for a project member, newest-edited first
export const NotionProjectDatabasePagesRequestConfig = new RequestBuilder({
  paramsSchema: NotionProjectDatabasePagesParamsSchema,
  path: (params) => `/integrations/notion/project/databases/${params.databaseId}/pages`,
  responseSchema: NotionDatabasePagesResponseSchema,
}).withIncludeProjectId();

// GET /integrations/linear - List org-level Linear integration configs
export const LinearIntegrationConfigsRequestConfig = new RequestBuilder({
  path: () => "/integrations/linear",
  responseSchema: LinearIntegrationConfigsResponseSchema,
}).withIncludeOrganizationId();

// POST /integrations/linear - Create Linear integration config from a customer application's credentials
export const CreateLinearIntegrationConfigRequestConfig = new RequestBuilder({
  path: () => "/integrations/linear",
  bodySchema: CreateLinearIntegrationConfigInputSchema,
  responseSchema: LinearIntegrationConfigSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

// DELETE /integrations/linear/configs/{integrationId} - Delete Linear integration config
export const DeleteLinearIntegrationConfigRequestConfig = new RequestBuilder({
  paramsSchema: LinearIntegrationConfigParamsSchema,
  path: (params) => `/integrations/linear/configs/${params.integrationId}`,
})
  .withMethod("DELETE")
  .withIncludeOrganizationId();

// PUT /integrations/linear/configs/{integrationId}/credentials - Replace the stored application credentials
export const UpdateLinearCredentialsRequestConfig = new RequestBuilder({
  paramsSchema: LinearIntegrationConfigParamsSchema,
  path: (params) => `/integrations/linear/configs/${params.integrationId}/credentials`,
  bodySchema: UpdateLinearCredentialsInputSchema,
  responseSchema: LinearIntegrationConfigSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

// GET /integrations/linear/configs/{integrationId}/webhook - Receiver URL to register, and whether its secret is stored
export const LinearWebhookRequestConfig = new RequestBuilder({
  paramsSchema: LinearIntegrationConfigParamsSchema,
  path: (params) => `/integrations/linear/configs/${params.integrationId}/webhook`,
  responseSchema: LinearWebhookInfoSchema,
}).withIncludeOrganizationId();

// PUT /integrations/linear/configs/{integrationId}/webhook/secret - Store the webhook's signing secret
export const UpdateLinearWebhookSecretRequestConfig = new RequestBuilder({
  paramsSchema: LinearIntegrationConfigParamsSchema,
  path: (params) => `/integrations/linear/configs/${params.integrationId}/webhook/secret`,
  bodySchema: UpdateLinearWebhookSecretInputSchema,
  responseSchema: LinearIntegrationConfigSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

// GET /integrations/linear/configs/{integrationId}/discover - Reachable teams and the projects on each
export const LinearDiscoverRequestConfig = new RequestBuilder({
  paramsSchema: LinearIntegrationConfigParamsSchema,
  path: (params) => `/integrations/linear/configs/${params.integrationId}/discover`,
  responseSchema: LinearDiscoverResponseSchema,
}).withIncludeOrganizationId();

// POST /integrations/linear/configs/{integrationId}/projects - Deploy to a project
export const AddLinearIntegrationProjectRequestConfig = new RequestBuilder({
  paramsSchema: LinearIntegrationConfigParamsSchema,
  path: (params) => `/integrations/linear/configs/${params.integrationId}/projects`,
  bodySchema: AddLinearIntegrationProjectInputSchema,
  responseSchema: LinearIntegrationConfigSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

// DELETE /integrations/linear/configs/{integrationId}/projects/{projectId} - Undeploy from a project
export const RemoveLinearIntegrationProjectRequestConfig = new RequestBuilder({
  paramsSchema: LinearIntegrationProjectParamsSchema,
  path: (params) => `/integrations/linear/configs/${params.integrationId}/projects/${params.projectId}`,
})
  .withMethod("DELETE")
  .withIncludeOrganizationId();

// PUT /integrations/linear/configs/{integrationId}/projects/{projectId}/sync-mode - Set a deployment's team selection
export const UpdateLinearSyncModeRequestConfig = new RequestBuilder({
  paramsSchema: LinearIntegrationProjectParamsSchema,
  path: (params) => `/integrations/linear/configs/${params.integrationId}/projects/${params.projectId}/sync-mode`,
  bodySchema: UpdateLinearSyncModeInputSchema,
  responseSchema: LinearIntegrationConfigSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

// GET /integrations/github - List org-level GitHub integration configs
export const GitHubIntegrationConfigsRequestConfig = new RequestBuilder({
  path: () => "/integrations/github",
  responseSchema: GitHubIntegrationConfigsResponseSchema,
}).withIncludeOrganizationId();

// POST /integrations/github - Create GitHub integration config from a customer App's credentials
export const CreateGitHubIntegrationConfigRequestConfig = new RequestBuilder({
  path: () => "/integrations/github",
  bodySchema: CreateGitHubIntegrationConfigInputSchema,
  responseSchema: GitHubIntegrationConfigSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

// DELETE /integrations/github/configs/{integrationId} - Delete GitHub integration config
export const DeleteGitHubIntegrationConfigRequestConfig = new RequestBuilder({
  paramsSchema: GitHubIntegrationConfigParamsSchema,
  path: (params) => `/integrations/github/configs/${params.integrationId}`,
})
  .withMethod("DELETE")
  .withIncludeOrganizationId();

// PUT /integrations/github/configs/{integrationId}/credentials - Replace the stored App credentials
export const UpdateGitHubCredentialsRequestConfig = new RequestBuilder({
  paramsSchema: GitHubIntegrationConfigParamsSchema,
  path: (params) => `/integrations/github/configs/${params.integrationId}/credentials`,
  bodySchema: UpdateGitHubCredentialsInputSchema,
  responseSchema: GitHubIntegrationConfigSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

// GET /integrations/github/configs/{integrationId}/webhook - Receiver URL to register, and whether its secret is stored
export const GitHubWebhookRequestConfig = new RequestBuilder({
  paramsSchema: GitHubIntegrationConfigParamsSchema,
  path: (params) => `/integrations/github/configs/${params.integrationId}/webhook`,
  responseSchema: GitHubWebhookInfoSchema,
}).withIncludeOrganizationId();

// PUT /integrations/github/configs/{integrationId}/webhook/secret - Store the webhook secret
export const UpdateGitHubWebhookSecretRequestConfig = new RequestBuilder({
  paramsSchema: GitHubIntegrationConfigParamsSchema,
  path: (params) => `/integrations/github/configs/${params.integrationId}/webhook/secret`,
  bodySchema: UpdateGitHubWebhookSecretInputSchema,
  responseSchema: GitHubIntegrationConfigSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

// GET /integrations/github/configs/{integrationId}/discover - Repositories the installation reaches
export const GitHubDiscoverRequestConfig = new RequestBuilder({
  paramsSchema: GitHubIntegrationConfigParamsSchema,
  path: (params) => `/integrations/github/configs/${params.integrationId}/discover`,
  responseSchema: GitHubDiscoverResponseSchema,
}).withIncludeOrganizationId();

// POST /integrations/github/configs/{integrationId}/projects - Deploy to a project
export const AddGitHubIntegrationProjectRequestConfig = new RequestBuilder({
  paramsSchema: GitHubIntegrationConfigParamsSchema,
  path: (params) => `/integrations/github/configs/${params.integrationId}/projects`,
  bodySchema: AddGitHubIntegrationProjectInputSchema,
  responseSchema: GitHubIntegrationConfigSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

// DELETE /integrations/github/configs/{integrationId}/projects/{projectId} - Undeploy from a project
export const RemoveGitHubIntegrationProjectRequestConfig = new RequestBuilder({
  paramsSchema: GitHubIntegrationProjectParamsSchema,
  path: (params) => `/integrations/github/configs/${params.integrationId}/projects/${params.projectId}`,
})
  .withMethod("DELETE")
  .withIncludeOrganizationId();

// PUT /integrations/github/configs/{integrationId}/projects/{projectId}/sync-mode - Set a deployment's repository selection
export const UpdateGitHubSyncModeRequestConfig = new RequestBuilder({
  paramsSchema: GitHubIntegrationProjectParamsSchema,
  path: (params) => `/integrations/github/configs/${params.integrationId}/projects/${params.projectId}/sync-mode`,
  bodySchema: UpdateGitHubSyncModeInputSchema,
  responseSchema: GitHubIntegrationConfigSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

// GET /integrations/slack/mentionables/channels?q= - Mentionable Slack channels (defaults on empty q)
export const SlackMentionableChannelsRequestConfig = new RequestBuilder({
  paramsSchema: MentionablesQueryParamsSchema,
  path: () => "/integrations/slack/mentionables/channels",
  responseSchema: MentionablesResponseSchema,
})
  .withQueryParams((params) => (params.q ? { q: params.q } : undefined))
  .withIncludeProjectId();

// GET /integrations/slack/mentionables/users?q= - Mentionable Slack users (defaults on empty q)
export const SlackMentionableUsersRequestConfig = new RequestBuilder({
  paramsSchema: MentionablesQueryParamsSchema,
  path: () => "/integrations/slack/mentionables/users",
  responseSchema: MentionablesResponseSchema,
})
  .withQueryParams((params) => (params.q ? { q: params.q } : undefined))
  .withIncludeProjectId();

// GET /integrations/google/mentionables/contacts?q= - Mentionable Google contacts (empty q returns no items)
export const GoogleMentionableContactsRequestConfig = new RequestBuilder({
  paramsSchema: MentionablesQueryParamsSchema,
  path: () => "/integrations/google/mentionables/contacts",
  responseSchema: MentionablesResponseSchema,
})
  .withQueryParams((params) => (params.q ? { q: params.q } : undefined))
  .withIncludeProjectId();

// GET /integrations/google/mentionables/labels - Mentionable Gmail labels (no query support)
export const GoogleMentionableLabelsRequestConfig = new RequestBuilder({
  path: () => "/integrations/google/mentionables/labels",
  responseSchema: MentionablesResponseSchema,
}).withIncludeProjectId();

// GET /integrations/google/mentionables/drive?q= - Mentionable Drive files/folders (empty q returns graph-ranked recents)
export const GoogleMentionableDriveRequestConfig = new RequestBuilder({
  paramsSchema: MentionablesQueryParamsSchema,
  path: () => "/integrations/google/mentionables/drive",
  responseSchema: MentionablesResponseSchema,
})
  .withQueryParams((params) => (params.q ? { q: params.q } : undefined))
  .withIncludeProjectId();

// GET /integrations/notion/mentionables/pages?q= - Mentionable Notion pages (defaults on empty q)
export const NotionMentionablePagesRequestConfig = new RequestBuilder({
  paramsSchema: MentionablesQueryParamsSchema,
  path: () => "/integrations/notion/mentionables/pages",
  responseSchema: MentionablesResponseSchema,
})
  .withQueryParams((params) => (params.q ? { q: params.q } : undefined))
  .withIncludeProjectId();

// GET /integrations/notion/mentionables/users?q= - Mentionable Notion users (defaults on empty q)
export const NotionMentionableUsersRequestConfig = new RequestBuilder({
  paramsSchema: MentionablesQueryParamsSchema,
  path: () => "/integrations/notion/mentionables/users",
  responseSchema: MentionablesResponseSchema,
})
  .withQueryParams((params) => (params.q ? { q: params.q } : undefined))
  .withIncludeProjectId();

// GET /integrations/linear/mentionables/issues?q= - Mentionable Linear issues (defaults on empty q)
export const LinearMentionableIssuesRequestConfig = new RequestBuilder({
  paramsSchema: MentionablesQueryParamsSchema,
  path: () => "/integrations/linear/mentionables/issues",
  responseSchema: MentionablesResponseSchema,
})
  .withQueryParams((params) => (params.q ? { q: params.q } : undefined))
  .withIncludeProjectId();

// GET /integrations/linear/mentionables/users?q= - Mentionable Linear users (defaults on empty q)
export const LinearMentionableUsersRequestConfig = new RequestBuilder({
  paramsSchema: MentionablesQueryParamsSchema,
  path: () => "/integrations/linear/mentionables/users",
  responseSchema: MentionablesResponseSchema,
})
  .withQueryParams((params) => (params.q ? { q: params.q } : undefined))
  .withIncludeProjectId();

// GET /integrations/github/mentionables/objects?q= - Mentionable GitHub issues and
// pull requests (defaults on empty q). One endpoint answers both kinds because
// GitHub's search returns them together.
export const GitHubMentionableObjectsRequestConfig = new RequestBuilder({
  paramsSchema: MentionablesQueryParamsSchema,
  path: () => "/integrations/github/mentionables/objects",
  responseSchema: MentionablesResponseSchema,
})
  .withQueryParams((params) => (params.q ? { q: params.q } : undefined))
  .withIncludeProjectId();

// GET /entities?kind=&provider=&q=&limit= - Browse the project's entity graph
export const EntitiesRequestConfig = new RequestBuilder({
  paramsSchema: EntitiesQueryParamsSchema,
  path: () => "/entities",
  responseSchema: EntitiesResponseSchema,
})
  .withQueryParams((params) => {
    const query: Record<string, string> = {};
    if (params.kind) query.kind = params.kind;
    if (params.provider) query.provider = params.provider;
    if (params.q) query.q = params.q;
    if (params.limit != null) query.limit = String(params.limit);
    return query;
  })
  .withIncludeProjectId();

// GET /entities/{id} - Entity detail with its graph connections
export const EntityDetailRequestConfig = new RequestBuilder({
  paramsSchema: EntityIdParamsSchema,
  path: (params) => `/entities/${params.id}`,
  responseSchema: EntityDetailResponseSchema,
}).withIncludeProjectId();
